import { isEqual, omit, cloneDeep } from 'lodash-es';
import { createGlobalState as _createGlobalState } from '@vueuse/core';
import Vue from 'vue';
import { withResolvers } from './../../../utils/withResolvers';
import {
  QueryImprovedCarouselImageForPreviewRequest,
  ImprovedProductImage,
  queryImprovedCarouselImageForPreview,
} from '@tt4b/ttam-hooks';
import { ref } from 'vue-demi';
import { KeyType, ResizeStrategy } from '@tt4b/ttam-enums';

export interface UpsertImproveTaskParams {
  creativeUuid: string;
  reqParams: QueryImprovedCarouselImageForPreviewRequest;
}

interface TaskData {
  reqParams?: QueryImprovedCarouselImageForPreviewRequest;
  result: ImprovedProductImage[];
  loading: boolean;
  isError: boolean;
  cancel?: () => void;
}

interface TaskMap {
  [creativeUuid: string]: TaskData | undefined;
}

function createGlobalState<T>(fn: () => T): () => T {
  let globalState: () => T;
  return () => {
    globalState = globalState ?? _createGlobalState(fn);
    return globalState();
  };
}

export const useImageSettingStore = createGlobalState(() => {
  const taskMap = ref<TaskMap>({});

  const startTask = async (params: UpsertImproveTaskParams, cancelPromise: Promise<never>) => {
    const { creativeUuid, reqParams } = params;
    try {
      const { improvedProductImages } = await Promise.race([
        queryImprovedCarouselImageForPreview(reqParams),
        cancelPromise,
      ]);
      Vue.set(taskMap.value, creativeUuid, {
        reqParams,
        result: improvedProductImages,
        loading: false,
        isError: false,
      });
    } catch (e) {
      const isError = e !== 'cancel';
      Vue.set(taskMap.value, creativeUuid, {
        result: [],
        loading: false,
        isError,
      });
      if (isError) {
        throw e;
      }
    }
  };

  const getformattedReqParams = (reqParams: QueryImprovedCarouselImageForPreviewRequest) => {
    const formattedReqParams = cloneDeep(reqParams);
    formattedReqParams.productIds.sort();
    return formattedReqParams;
  };

  const cancelImproveTask = (creativeUuid: string) => {
    const oldTask = taskMap.value[creativeUuid];
    oldTask?.cancel?.();
    Vue.delete(taskMap.value, creativeUuid);
  };

  const upsertImproveTask = (params: UpsertImproveTaskParams) => {
    const { creativeUuid, reqParams } = params;
    const { keyType, resizeStrategy } = reqParams.carouselImageInfo;

    const taskReq = taskMap.value[creativeUuid]?.reqParams;
    const isSameReq = taskReq ? isEqual(getformattedReqParams(taskReq), getformattedReqParams(reqParams)) : false;
    if (isSameReq) {
      return;
    }

    cancelImproveTask(creativeUuid);
    if (keyType === KeyType.Disabled && resizeStrategy === ResizeStrategy.Original) {
      return;
    }
    if (reqParams.productIds.length === 0) {
      return;
    }

    const { promise: cancelPromise, reject } = withResolvers<never>();
    const cancel = () => reject('cancel');
    Vue.set(taskMap.value, creativeUuid, {
      reqParams,
      result: [],
      loading: true,
      isError: false,
      cancel,
    });
    startTask(params, cancelPromise);
  };

  const getImproveTaskStatus = (creativeUuid?: string): Omit<TaskData, 'cancel' | 'reqParams'> => {
    const taskData: TaskData = taskMap.value[creativeUuid] ?? { result: [], loading: false, isError: false };
    return omit(taskData, 'cancel', 'reqParams');
  };

  return {
    upsertImproveTask,
    cancelImproveTask,
    getImproveTaskStatus,
  };
});
